import { isPromise } from 'bold-ui'
import { Decorator, FormApi, FormState } from 'final-form'
import createFocusOnErrorDecorator from 'final-form-focus'
import { get } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AnyObject,
  Form as FinalForm,
  FormProps as FinalFormProps,
  FormRenderProps as FinalFormRenderProps,
  useForm,
} from 'react-final-form'
import { emptyArrayOf } from 'util/array'
import { isObjectDeepEmpty, isObjectDeepEqualById } from 'util/object'
import { isEmpty } from 'util/validation/Util'

import { defaultTransformResult } from './util-form'

export type FormRenderProps<FormValues = AnyObject> = FinalFormRenderProps<FormValues>

export type ResultType = object | Promise<object | undefined> | undefined | void

interface InitialDirtyFieldsWithCustomNames<T> {
  values: T
  names?: string[]
}

export interface FormProps<FormValues> extends FinalFormProps<FormValues> {
  focusOnError?: boolean
  initialDirtyFields?: FormValues | InitialDirtyFieldsWithCustomNames<FormValues>
  transformResult?(
    result: ResultType,
    supressNotificationError?: boolean,
    suppressValidationError?: boolean,
    supressFieldValidationNotificationError?: boolean
  ): ResultType
  onSubmitSucceeded?(formState: FormState<FormValues>): void
  onSubmitFailed?(formState: FormState<FormValues>, supressNotificationError?: boolean): void
  resetFormToInitialValues?: boolean
  suppressNotificationError?: boolean
  suppressValidationError?: boolean
  supressFieldValidationNotificationError?: boolean
}

const focusOnErrorDecorator = createFocusOnErrorDecorator()

export function Form<FormValues extends object = any>(props: Readonly<FormProps<FormValues>>) {
  const {
    focusOnError,
    initialDirtyFields,
    transformResult = defaultTransformResult,
    onSubmitFailed,
    onSubmitSucceeded,
    onSubmit,
    render,
    resetFormToInitialValues = false,
    suppressNotificationError = false,
    suppressValidationError = false,
    supressFieldValidationNotificationError = true,
    ...rest
  } = props

  const resetForm = useRef(false)
  resetForm.current = resetFormToInitialValues

  const renderForm = useCallback(
    (formRenderProps: FormRenderProps<FormValues>) => {
      const { form, handleSubmit } = formRenderProps

      const normalizedInitialDirtyFields: InitialDirtyFieldsWithCustomNames<FormValues> = isInitialDirtyFieldsWithCustomNames(
        initialDirtyFields
      )
        ? initialDirtyFields
        : { values: initialDirtyFields }

      if (resetForm.current) {
        setTimeout(() => form.getRegisteredFields().forEach((field) => form.resetFieldState(field)))
        setTimeout(form.reset)
        resetForm.current = false
      }

      const handleSubmitWrapper = (event) => {
        if (onSubmitFailed && !isObjectDeepEmpty(form.getState().errors)) {
          setTimeout(() => onSubmitFailed(form.getState(), suppressNotificationError))
        }
        return handleSubmit(event)
      }

      return (
        <>
          {normalizedInitialDirtyFields?.values && (
            <FormDirtyValuesSetter initialDirtyFields={normalizedInitialDirtyFields} />
          )}
          {render({
            ...formRenderProps,
            handleSubmit: handleSubmitWrapper,
          })}
        </>
      )
    },
    [initialDirtyFields, onSubmitFailed, render, suppressNotificationError]
  )

  const formOnSubmit = useCallback(
    (values: FormValues, form: FormApi<FormValues>) => {
      const emitSubmitEvents = (submitResult: ResultType, form: FormApi<FormValues>) => {
        if (!submitResult && onSubmitSucceeded) {
          setTimeout(() => onSubmitSucceeded(form.getState()))
        }

        if (submitResult && onSubmitFailed) {
          setTimeout(() => onSubmitFailed(form.getState(), suppressNotificationError))
        }
      }

      const result = onSubmit(values, form)
      let ret = transformResult(
        result,
        suppressNotificationError,
        suppressValidationError,
        supressFieldValidationNotificationError
      )

      if (isPromise(ret)) {
        ret = ret.then((res) => {
          emitSubmitEvents(res, form)
          return res
        })
      } else {
        emitSubmitEvents(ret, form)
      }

      return ret
    },
    [
      onSubmit,
      onSubmitFailed,
      onSubmitSucceeded,
      suppressNotificationError,
      suppressValidationError,
      transformResult,
      supressFieldValidationNotificationError,
    ]
  )

  const externalDecorators = props.decorators ?? emptyArrayOf<Decorator<FormValues>>()
  const decorators = useMemo(() => [...externalDecorators, ...(focusOnError ? [focusOnErrorDecorator] : [])], [
    externalDecorators,
    focusOnError,
  ])

  return <FinalForm<FormValues> {...rest} onSubmit={formOnSubmit} render={renderForm} decorators={decorators} />
}

function isInitialDirtyFieldsWithCustomNames<T>(
  obj: T | InitialDirtyFieldsWithCustomNames<T>
): obj is InitialDirtyFieldsWithCustomNames<T> {
  return obj && (obj as InitialDirtyFieldsWithCustomNames<T>).values !== undefined
}

Form.defaultProps = {
  focusOnError: true,
  decorators: [],
} as Partial<FormProps<any>>

function FormDirtyValuesSetter<T>(props: { initialDirtyFields: InitialDirtyFieldsWithCustomNames<T> }) {
  const { values, names } = props.initialDirtyFields
  const [isFilled, setIsFilled] = useState(false)

  const { batch, change, getRegisteredFields, getFieldState } = useForm()

  const fieldsRegistered = !!getRegisteredFields().length
  const fieldPaths = names ?? Object.keys(values)

  useEffect(() => {
    if (values && fieldsRegistered && !isFilled) {
      batch(() => {
        fieldPaths.forEach((fieldPath) => {
          const dirtyInitialValue = get(values, fieldPath)
          const field = getFieldState(fieldPath)

          if (!field?.value) {
            if (dirtyInitialValue) change(fieldPath, dirtyInitialValue)
          } else if (!field.dirty && !areFormValuesEqual(field.initial, dirtyInitialValue)) {
            field.change(dirtyInitialValue)
            field.blur()
          }
        })
      })
      setIsFilled(true)
    }
  }, [batch, change, fieldPaths, fieldsRegistered, getFieldState, isFilled, values])

  return null
}

const normalizeEmptyValue = (value: any) => (!isEmpty(value) ? value : undefined)
const areFormValuesEqual = (oldValue: any, newValue: any) => {
  const a = normalizeEmptyValue(oldValue)
  const b = normalizeEmptyValue(newValue)
  return typeof b === 'object' ? isObjectDeepEqualById(a, b) : '' + a === '' + b
}
